import { useQuery } from "@/services/api";
import { isAdmin } from "@/utils/users";
import { useQueryClient } from "@tanstack/react-query";
import { atom, useAtom } from "jotai";
import { useEffect, useRef } from "react";

const currentUserLoading = atom<boolean>(false);
const currentUserError = atom<any>(false);

type Options = {
  refetch?: boolean;
};

export const useCurrentUser = (options: Options = {}) => {
  const [loading, setLoading] = useAtom(currentUserLoading);
  const [error, setError] = useAtom(currentUserError);
  const refetchRef = useRef(options?.refetch);
  const queryClient = useQueryClient();

  // fetch only once and rely on cache always
  const {
    data: user,
    isLoading,
    isError,
    refetch,
  } = useQuery<any>("/user", {
    queryKey: ["user"],
    cacheTime: 30 * 60 * 1000,
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const fetchCurrentUserConditionally = async () => {
    if (refetchRef.current) return await refetch();
    if (isError) {
      setError(user);
      return null;
    } else {
      return user;
    }
  };

  const fetchCurrentUser = async () => {
    if ((window as any)?.currentUserLoading) return;
    (window as any).currentUserLoading = true;
    setLoading(true);
    setError(null);
    try {
      const userData = await fetchCurrentUserConditionally();
      return userData;
    } catch (error) {
      console.error("Error fetching user:", error);
      setError(error);
      queryClient.resetQueries({ queryKey: ["user"] });
    } finally {
      setLoading(false);
      (window as any).currentUserLoading = false;
    }
  };

  useEffect(() => {
    if (refetchRef.current || !user || Object.keys(user).length === 0) {
      fetchCurrentUser();
      refetchRef.current = false;
    }
  }, [user]);

  const clearUserData = () => {
    queryClient.resetQueries({ queryKey: ["user"] });
    queryClient.clear();
  };

  const getInitials = () => {
    if (!user || !user.name) return "Gu"; // guest

    const splitTokens = user.name
      .split(" ")
      .map((m) => m.trim())
      .filter((f) => f);

    if (splitTokens.length > 1) {
      return splitTokens[0][0].toUpperCase() + splitTokens[1][0].toUpperCase();
    }
    return splitTokens[0][0].toUpperCase() + splitTokens[0][1].toLowerCase();
  };

  const canSeeMultipleStores = isAdmin(user) || user?.builders?.length > 1;

  const isAdminUser = isAdmin(user);

  return {
    user,
    loading,
    error,
    fetchCurrentUser,
    fetchCurrentUserConditionally,
    clearUserData,
    isAdminUser,
    canSeeMultipleStores,
    getInitials,
  };
};
